'use client';

import { DeleteOutlined as DeleteIcon, Edit as EditIcon, History as HistoryIcon } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { Button, IconButton, Tooltip } from '@mui/material';
import {
	DataGridPro,
	GridPaginationModel,
	GridRowModesModel,
	GridSortModel,
	GridToolbarColumnsButton,
	GridToolbarContainer,
	GridToolbarDensitySelector,
	GridToolbarFilterButton,
	getGridDateOperators,
	getGridNumericOperators,
} from '@mui/x-data-grid-pro';
import { useLazyQuery, useMutation, useQuery } from '@parkchirp/api-client';
import { useSearchParams } from 'next/navigation';
import { ResourceTypes } from 'parkchirp-shared/enums';
import { FC, ReactNode, useContext, useEffect, useState } from 'react';
import { CommonContext } from '../../contexts';
import { useDebounce } from '../../hooks/useDebounce';
import { DELETE_TAX_EXEMPT_CERTIFICATE_V2 } from '../../mutations';
import { GET_PRESIGNED_URL, GET_TAX_EXEMPT_CERTIFICATES_CSV, LIST_TAX_EXEMPT_CERTIFICATES } from '../../queries';
import { SetShowDialog, SetShowSnackbar, SnackbarParams } from '../../types';
import { dayjs, stateFieldOptions } from '../../utils';
import { datagridFilterPanelAttributes } from '../../utils/cypress';
import { Snackbar } from '../alerts';
import { AuditHistoryModal } from '../audit';
import { DatagridLinearProgress } from '../loading/datagridLinearProgress';
import { Dialog } from '../modals';
import { ConfirmationInputModal } from '../modals/confirmationInputModal';
import { TaxExemptCertificateUploadModal } from '../modals/taxExemptCertificateUpload';
import { CsvExportButtonV2 } from '../reports/csvExportButtonV2';

const numericOperators = getGridNumericOperators();
const dateOperators = getGridDateOperators();

type TaxExemptProps = {
	showSnackbar?: Partial<SnackbarParams>;
	setShowSnackbar?: SetShowSnackbar;
	showDialog?: ReactNode | null;
	setShowDialog?: SetShowDialog;
	initialData?: any;
	initialCount?: number;
};

const ActionWrapper = ({ children }) => (
	<div
		style={{
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
			width: '100%',
		}}
	>
		{children}
	</div>
);

const convertFilterItems = (filterItems) =>
	filterItems
		.filter(
			({ field, operator, value }) =>
				value || field === 'taxExempt' || ['isEmpty', 'isNotEmpty'].includes(operator),
		)
		.map(({ field, operator, value }) => ({
			propertyName: field,
			op: operator,
			value,
		}));

const CustomToolbar = ({ filters, sortOrder, setShowDialog, setShowSnackbar, refetch }) => (
	<GridToolbarContainer>
		<GridToolbarFilterButton />
		<GridToolbarColumnsButton />
		<GridToolbarDensitySelector />
		<CsvExportButtonV2
			query={GET_TAX_EXEMPT_CERTIFICATES_CSV}
			queryName='getTaxExemptCertificatesCsv'
			args={{
				variables: {
					filters,
					order: sortOrder,
				},
				context: {
					apiVersion: 'v2',
				},
			}}
		/>
		<Button
			size='small'
			variant='outlined'
			color='primary'
			startIcon={<AddIcon />}
			onClick={() =>
				setShowDialog(
					<TaxExemptCertificateUploadModal
						singleSubscriptionData={null}
						setShowSnackbar={setShowSnackbar}
						setShowDialog={setShowDialog}
						refetch={refetch}
					/>,
				)
			}
		>
			Add Certificate
		</Button>
	</GridToolbarContainer>
);

export const TaxExemptTable: FC<TaxExemptProps> = ({
	showSnackbar: showSnackbarDefault = null,
	setShowSnackbar: setShowSnackbarDefault = null,
	showDialog: showDialogDefault = null,
	setShowDialog: setShowDialogDefault = null,
	initialData = [],
	initialCount = 0,
}) => {
	const { hasComponentAccess } = useContext(CommonContext);
	const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
	const defaultSort = {
		sort: 'desc',
		field: 'createdAt',
	};
	const [{ pageSize, page }, setPaginationModel] = useState<GridPaginationModel>({ pageSize: 100, page: 0 });
	const [sortOrder, setSortOrder] = useState(defaultSort);
	const [rows, setRows] = useState(initialData);
	const [rowCount, setRowCount] = useState(initialCount);
	const [deleteTaxExemptCert] = useMutation(DELETE_TAX_EXEMPT_CERTIFICATE_V2);
	const [selectedSubscription, setSelectedSubscription] = useState(null);
	const [loading, setLoading] = useState(!initialData.length);
	const [showSnackbar, setShowSnackbar = setShowSnackbarDefault] = useState(showSnackbarDefault);
	const [showDialog, setShowDialog = setShowDialogDefault] = useState(showDialogDefault);
	const [initialLoadFlag, setInitialLoadFlag] = useState(!!initialData.length);
	const accountIdSearchParam = useSearchParams().get('accountId');
	const defaultFilters = accountIdSearchParam
		? [
				{
					propertyName: 'accountId',
					op: 'equals',
					value: accountIdSearchParam,
				},
			]
		: [];
	const [filters, setFilters] = useState(defaultFilters);
	const debouncedSetFilters = useDebounce(setFilters, 500);

	const { data, refetch } = useQuery(LIST_TAX_EXEMPT_CERTIFICATES, {
		variables: {
			limit: pageSize,
			offset: page * pageSize,
			order: sortOrder,
			filters,
		},
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		ssr: false,
		notifyOnNetworkStatusChange: true,
		skip: initialLoadFlag,
	});
	const [getPresignedUrl] = useLazyQuery(GET_PRESIGNED_URL, {
		fetchPolicy: 'network-only',
	});

	const handleClick = async (filePath) => {
		try {
			const response = await getPresignedUrl({
				variables: { bucket: 'parkchirp-files', s3Key: filePath, addEnv: true },
				context: {
					apiVersion: 'v2',
					appName: process.env.NEXT_PUBLIC_APP_NAME,
				},
			});

			const { presignedUrl } = response.data.getPresignedUrl;
			window.location.href = presignedUrl;
		} catch (err) {
			console.log('err', err);
			setShowSnackbar({
				message: 'Could not download this certificate',
				severity: 'error',
			});
		}
	};

	useEffect(() => {
		setLoading(true);
		if (data?.listTaxExemptCertificates) {
			const certificatesRows = data?.listTaxExemptCertificates.taxExemptCertificates.map((certificate: any) => ({
				id: certificate.id,
				facilityId: certificate.facilityId,
				facilityNumber: certificate.facilityNumber,
				facilityName: certificate.facilityName,
				accountId: `AC-${certificate.accountId}`,
				subId: certificate.payorId,
				subscriptionName: certificate.subscriptionName,
				subStatus: certificate.subStatus,
				certId: certificate.id,
				issueState: certificate.issueState,
				taxExempt: certificate.taxExempt,
				subscriptionId: certificate.subscriptionId,
				companyName: certificate.companyName,
				issueDate: dayjs.utc(certificate.issueDate).format('MM/DD/YYYY'),
				filePath: certificate.filePath,
				expirationDate: dayjs.utc(certificate.expirationDate).format('MM/DD/YYYY'),
				exemptionNumber: certificate.exemptionNumber,
				organizationType: certificate.organizationType,
				organizationId: certificate.organizationId,
				createdAt: dayjs.utc(certificate.createdAt).format('MM/DD/YYYY'),
				timezone: certificate.timezone,
				subscriptionTaxExemptTaxes: certificate.subscriptionTaxExemptTaxes,
			}));
			setRows(certificatesRows);
			setRowCount(data.listTaxExemptCertificates.total);
			setLoading(false);
		}
	}, [data]);

	const onSortModelChange = (sortModel: GridSortModel) => {
		setInitialLoadFlag(false);
		setLoading(true);
		if (!sortModel.length) {
			return setSortOrder(sortOrder);
		}

		const { field, sort } = sortModel[0];

		return setSortOrder({ field, sort });
	};

	const onFilterModelChange = (filterModel) => {
		setInitialLoadFlag(false);
		setLoading(true);
		setPaginationModel((prev) => ({ ...prev, page: 0 }));

		const { items: filterItems } = filterModel;

		if (!filterItems.length) {
			debouncedSetFilters([]);
			return;
		}
		const convertedFilters = convertFilterItems(filterModel.items);

		if (convertedFilters.length) debouncedSetFilters(convertedFilters);
	};

	const handleDeleteCert = async (certId, facilityId, subscriptionId, index) => {
		try {
			await deleteTaxExemptCert({
				variables: {
					certId,
					facilityId,
					subscriptionId,
				},
				context: {
					appName: process.env.NEXT_PUBLIC_APP_NAME,
					apiVersion: 'v2',
				},
			});
			const rowsCopy = [...rows];
			rowsCopy.splice(index, 1);
			const newRows = rowsCopy.map((s, numIndex) => {
				s.id = numIndex;
				return s;
			});
			setRows([...newRows]);
			setShowSnackbar({
				message: 'Successfully deleted this certificate',
				severity: 'success',
			});
			setShowDialog(null);
		} catch {
			setShowSnackbar({
				message: 'Could not delete this certificate',
				severity: 'error',
			});
		}
	};

	const handleAuditHistory = async (id: string) => {
		const auditDialog = (
			<AuditHistoryModal
				title='Tax Exempt Certificate Audit History'
				resourceId={id}
				resourceType={ResourceTypes.TAX_EXEMPT_CERTIFICATE}
				onClose={() => {
					setShowDialog(null);
				}}
			/>
		);

		setShowDialog(auditDialog);
	};

	const columns = [
		{
			field: 'Actions',
			headerName: 'Actions',
			sortable: false,
			filterable: false,
			renderCell: (params) => (
				<ActionWrapper>
					<EditIcon
						onClick={() =>
							setShowDialog(
								<TaxExemptCertificateUploadModal
									singleSubscriptionData={params.row}
									setShowSnackbar={setShowSnackbar}
									setShowDialog={setShowDialog}
									refetch={refetch}
								/>,
							)
						}
					/>
					{hasComponentAccess('AuditHistoryTable') && (
						<IconButton
							onClick={() => {
								handleAuditHistory(params.row.id);
							}}
						>
							<Tooltip title='View Audit History'>
								<HistoryIcon />
							</Tooltip>
						</IconButton>
					)}
					<DeleteIcon
						onClick={() => {
							const cancelFunction = () => {
								setShowDialog(null);
							};
							const certAccountId = params.row.accountId;
							const confirmationInputModal = (
								<ConfirmationInputModal
									title={`Are you sure you want to delete the certificate for ${certAccountId}?`}
									validationString='DELETE'
									dismissText='Cancel'
									confirmText='Confirm'
									handleDismiss={cancelFunction}
									handleConfirm={() =>
										handleDeleteCert(
											params.row.certId,
											params.row.facilityId,
											params.row.subscriptionId,
											params.row.id,
										)
									}
								/>
							);
							setShowDialog(confirmationInputModal);
						}}
					/>
				</ActionWrapper>
			),
		},
		{
			field: 'accountId',
			headerName: 'Acct. ID',
			filterOperators: numericOperators,
		},
		{
			field: 'subId',
			headerName: 'Sub. ID',
			width: 70,
			filterOperators: numericOperators,
		},
		{
			field: 'subStatus',
			headerName: 'Sub. Status',
			width: 120,
			type: 'singleSelect',
			valueOptions: [
				{ value: 'Active', label: 'Active' },
				{ value: 'Pending', label: 'Pending' },
				{ value: 'Terminated', label: 'Terminated' },
				{ value: 'Suspended', label: 'Suspended' },
			],
			// disabling for now patim 4/1/25
			filterable: false,
			sortable: false,
		},
		{
			field: 'subscriptionName',
			headerName: 'Sub. Name',
			width: 150,
		},
		{
			field: 'facilityNumber',
			headerName: 'Facility #',
		},
		{
			field: 'facilityName',
			headerName: 'Facility Name',
			width: 200,
		},
		{
			field: 'taxExempt',
			headerName: 'Tax Exempt',
			type: 'singleSelect',
			valueOptions: [
				{ value: 'No', label: 'No' },
				{ value: 'Manual', label: 'Manual' },
				{ value: 'Certificate', label: 'Certificate' },
			],
		},
		{
			field: 'dateAdded',
			headerName: 'Date Added',
			renderCell: (params) => dayjs(params.row.createdAt).format('MM/DD/YYYY'),
			filterOperators: dateOperators,
		},
		{
			field: 'companyName',
			headerName: 'Company Name',
			editable: true,
		},
		{
			field: 'issueState',
			headerName: 'Issue State',
			editable: true,
			type: 'singleSelect',
			valueOptions: stateFieldOptions,
			valueGetter: (params) => (params.row.issueState !== undefined ? params.row.issueState : 'Alabama'),
		},
		{
			field: 'issueDate',
			headerName: 'Issue Date',
			editable: true,
			type: 'date',
			filterOperators: dateOperators,
			valueGetter: (params) => dayjs.utc(params.row.issueDate, 'MM/DD/YYYY').toDate(),
			renderCell: (params) => params.row.issueDate,
		},
		{
			field: 'expirationDate',
			headerName: 'Expiration Date',
			editable: true,
			type: 'date',
			filterOperators: dateOperators,
			cellClassName: (params) => {
				const timezone = params.row.timezone || 'UTC'; // Default to 'UTC' if timezone is not set
				const date1 = dayjs().tz(timezone); // Current date in the specified timezone
				const date2 = dayjs(params.row.expirationDate).tz(timezone); // Expiration date in the specified timezone
				if (date1.isAfter(date2, 'day')) {
					return 'errorBorder';
				}
				return '';
			},
			valueGetter: (params) => dayjs.utc(params.row.expirationDate, 'MM/DD/YYYY').toDate(),
			renderCell: (params) => params.row.expirationDate,
		},
		{
			field: 'organizationType',
			headerName: 'Organization Type',
			editable: true,
		},
		{
			field: 'exemptionNumber',
			headerName: 'Exemption Number',
			editable: true,
		},
		{
			field: 'certificate',
			headerName: 'Certificate',
			filterable: false,
			renderCell: (params) => {
				if (params.row.filePath) {
					return <InsertDriveFileIcon onClick={() => handleClick(params.row.filePath)} />;
				}
				return null;
			},
		},
	];

	const initialState = accountIdSearchParam
		? {
				filter: {
					filterModel: {
						items: [
							{
								field: 'accountId',
								operator: '=',
								value: Number(accountIdSearchParam),
							},
						],
					},
				},
			}
		: undefined;

	return (
		<div className='pca__datagrid-sticky--headfoot' data-cy='tax-exempt-certificates-datagrid-container'>
			<DataGridPro
				sx={{
					'& .MuiDataGrid-columnHeaderTitle': {
						textOverflow: 'clip',
						whiteSpace: 'break-spaces',
						lineHeight: 'normal',
					},
					'& .MuiBadge-badge': {
						display: 'none',
					},
					height: '100%',
				}}
				slots={{
					toolbar: CustomToolbar,
					loadingOverlay: DatagridLinearProgress,
				}}
				rows={rows || []}
				editMode='row'
				rowModesModel={rowModesModel}
				columns={columns}
				rowCount={rowCount}
				paginationModel={{ page, pageSize }}
				pageSizeOptions={[pageSize]}
				loading={loading}
				hideFooter={false}
				pagination
				onRowClick={(e) =>
					setSelectedSubscription({
						accountId: e.row.accountId,
						subscriptionId: e.row.subscriptionId,
						facilityName: e.row.facilityName,
						facilityNumber: e.row.facilityNumber,
						subscriptionName: e.row.subscriptionName,
						facilityId: e.row.facilityId,
						organizationId: e.row.organizationId,
					})
				}
				paginationMode='server'
				onPaginationModelChange={(newModel) => {
					setInitialLoadFlag(false);
					setLoading(true);
					setPaginationModel(newModel);
				}}
				sortingMode='server'
				onSortModelChange={onSortModelChange}
				filterMode='server'
				onFilterModelChange={onFilterModelChange}
				slotProps={{
					filterPanel: { ...datagridFilterPanelAttributes },
					toolbar: {
						selectedSubscription,
						rows,
						filters,
						sortOrder,
						setRows,
						setRowModesModel,
						setSelectedSubscription,
						setShowDialog,
						setShowSnackbar,
						refetch,
					},
				}}
				initialState={initialState}
			/>
			{!showSnackbarDefault && <Snackbar showSnackbar={showSnackbar} setShowSnackbar={setShowSnackbar} />}
			{!showDialogDefault && <Dialog showDialog={showDialog} setShowDialog={setShowDialog} />}
		</div>
	);
};
